import { useAppSelector } from "@/app/appStore";
import { Schedule, insightsApi } from "@/shared/api";
import { findCurrentSchedule } from "@/shared/helpers/findCurrentSchedule";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { usePlayerStore } from "@tezzro/player";

export function useSchedules({
  queryKey,
}: {
  queryKey:
    | "planogramSchedules"
    | "zoneHistorySchedules"
    | "calibrationSchedules";
}) {
  const planogramDateRange = useAppSelector(
    (state) => state.annotation.dateRange
  );
  const { getState, subscribe } = usePlayerStore();

  const { data } = insightsApi.endpoints[queryKey].useQuery(
    {
      from_datetime: planogramDateRange.start?.toISOString(),
      to_datetime: planogramDateRange.end?.toISOString(),
    },
    {
      skip: !planogramDateRange.start || !planogramDateRange.end,
    }
  );

  const [activeSchedule, setActiveSchedule] = useState<Schedule | null>(null);
  const isMounted = useRef(false);

  // Keep active schedule up-to-date with currentTime
  // Keep active schedule up-to-date with currentTime
  useEffect(() => {
    const unsubscribe = subscribe((state, prevState) => {
      if (state.currentTime !== prevState.currentTime) {
        const currentTime = state.currentTime;

        if (!data || !currentTime) return;

        try {
          const currentSchedule = findCurrentSchedule(
            data.results,
            currentTime
          );

          if (currentSchedule && currentSchedule?.id !== activeSchedule?.id) {
            setActiveSchedule(currentSchedule);
          }
        } catch (err) {
          toast.error((err as Error).message, {
            id: "schedule-error",
            duration: 1000 * 10,
          });
          unsubscribe(); // Stop listening to currentTime
        }
      }
    });

    return unsubscribe;
  }, [data, activeSchedule?.id, subscribe]);

  // Set latest schedule as default
  useEffect(() => {
    if (isMounted.current || !data) return;

    const currentTime = getState().currentTime || Date.now();

    try {
      const defaultSchedule = findCurrentSchedule(data.results, currentTime);

      if (defaultSchedule) {
        setActiveSchedule(defaultSchedule);
        isMounted.current = true;
      }
    } catch (err) {
      toast.error(`Cannot display schedule: ${(err as Error).message}`, {
        id: "schedule-error",
        duration: 1000 * 10,
      });
    }
  }, [data, getState]);

  return { activeSchedule, schedules: data?.results };
}
